// One widget per parameter type (spec/dashboard.md section 6.4): the scalars here, the pickers and the composite editors
// in their own files; `compact` is the grid cell's form.
import { useMemo, useState } from 'react';
import type { Json } from '../../api/types';
import { Button } from '../../ui/Button';
import { Combobox } from '../../ui/Combobox';
import { Pencil } from '../../ui/icons';
import { Checkbox, CommitTextInput, NumberInput, Select, TextArea } from '../../ui/inputs';
import { COMMON_DURATION_PRESETS, ColorInput, DateTimeInput, DurationInput } from '../../ui/values';
import { useGame } from '../../shell/GameContext';
import { useContent } from '../ContentContext';
import { zeroWord } from '../format';
import { ConditionField } from '../conditions/ConditionBuilder';
import { isConditionTarget } from '../conditions/model';
import { AssetPicker } from './AssetPicker';
import { PriceField, REWARD_DROP_TEMPLATE, RewardDropsField, RewardField, isEconomyObject } from './economy';
import { ComponentField } from './ComponentField';
import { isComponentTarget, useEditorHost, type WidgetProps } from './context';
import { InlineObjectField } from './InlineObjectField';
import { ListField } from './ListField';
import { LocalizedStringPicker } from './LocalizedStringPicker';
import { ProductSelect } from './ProductSelect';
import { ReferenceField } from './ReferenceField';

export function FieldWidget(props: WidgetProps) {
  const { param: p, value, onCommit, onCancel, disabled, autoFocus, compact, id } = props;
  const catalog = useContent();
  const { offsets } = useGame();
  const host = useEditorHost();
  const size = compact ? 's' : 'm';
  const label = p.displayName ?? p.name;
  const escape = (e: { key: string }) => e.key === 'Escape' && onCancel?.();
  switch (p.type) {
    case 'string':
      return <CommitTextInput id={id} size={size} value={typeof value === 'string' ? value : ''} regex={p.regex} multiline={!compact && (p.regex ? false : undefined)} onCommit={(v) => onCommit(v)} disabled={disabled} autoFocus={autoFocus} aria-label={label} onKeyDown={escape} />;
    case 'int':
    case 'long':
    case 'float':
      return <NumberInput id={id} size={size} kind={p.type} value={typeof value === 'number' || typeof value === 'string' ? value : null} onCommit={(v) => onCommit(v as Json)} disabled={disabled} autoFocus={autoFocus} aria-label={label} onKeyDown={escape} />;
    case 'bool':
      return <Checkbox id={id} checked={Boolean(value)} onChange={(e) => onCommit(e.target.checked)} disabled={disabled} autoFocus={autoFocus} aria-label={label} />;
    case 'enum': {
      const e = p.enum ? catalog.enums.get(p.enum) : undefined;
      if (!e) return <NumberInput id={id} size={size} kind="int" value={typeof value === 'number' ? value : 0} onCommit={(v) => onCommit(v as Json)} disabled={disabled} aria-label={label} />;
      if (e.isFlags) {
        const n = Number(value ?? 0);
        return (
          <span className="row wrap" role="group" aria-label={label}>
            {e.values.filter((v) => Number(v.value) !== 0).map((v) => (
              <Checkbox key={v.name} label={v.displayName ?? v.name} checked={(n & Number(v.value)) === Number(v.value)} disabled={disabled} onChange={(ev) => onCommit(ev.target.checked ? n | Number(v.value) : n & ~Number(v.value))} />
            ))}
            {e.values.filter((v) => Number(v.value) !== 0).length === 0 && <span className="muted small">none</span>}
          </span>
        );
      }
      return (
        <Select id={id} size={size} value={String(value ?? '')} onChange={(ev) => onCommit(Number(ev.target.value))} disabled={disabled} autoFocus={autoFocus} aria-label={label}>
          {e.values.map((v) => (
            <option key={v.name} value={String(v.value)}>
              {v.displayName ?? v.name}
            </option>
          ))}
        </Select>
      );
    }
    case 'duration':
      // a form shows the usual windows as chips; a grid cell keeps the four boxes only
      return <DurationInput id={id} value={typeof value === 'number' ? value : 0} onCommit={(s) => onCommit(s)} zeroMeans={zeroWord(p)} presets={compact ? undefined : COMMON_DURATION_PRESETS} disabled={disabled} aria-label={label} />;
    case 'date_time':
      return <DateTimeInput id={id} value={typeof value === 'number' ? value : null} onCommit={(s) => onCommit(s ?? 0)} ctx={{ offsets }} disabled={disabled} aria-label={label} />;
    case 'color':
      return <ColorInput id={id} value={typeof value === 'string' ? value : null} onCommit={(v) => onCommit(v)} disabled={disabled} aria-label={label} />;
    case 'localized_string':
      return <LocalizedStringPicker {...props} />;
    case 'document':
      if (isConditionTarget(p.template)) return <ConditionField {...props} />;
      return isComponentTarget(catalog, p) ? compact ? <EditInDocument {...props} /> : <ComponentField {...props} /> : <ReferenceField {...props} />;
    case 'asset':
      return <AssetPicker {...props} />;
    case 'product':
      return <ProductSelect {...props} />;
    case 'object': {
      const economy = isEconomyObject(p);
      if (economy === 'price') return <PriceField {...props} />;
      if (economy === 'reward') return <RewardField {...props} />;
      return compact ? <EditInDocument {...props} /> : <InlineObjectField {...props} />;
    }
    case 'list':
      if (p.of?.type === 'object' && p.of.template === REWARD_DROP_TEMPLATE) return <RewardDropsField {...props} />;
      return compact ? <EditInDocument {...props} /> : <ListField {...props} />;
    case 'template_ref':
      return <TemplateRefSelect {...props} />;
    case 'profile_path':
      return <ProfilePathPicker {...props} />;
    case 'profile_data':
      return (
        <code className="small muted" title="Profile data is edited on the player's page.">
          {value === undefined || value === null ? 'null' : JSON.stringify(value).slice(0, 160)}
        </code>
      );
    default:
      return <JsonField {...props} />;
  }
  // unreachable, keeps the host in scope for the compact fallbacks
  void host;
}

/** A cell whose type needs the editor: opens the owning document at that field. */
function EditInDocument({ param: p, value, owner }: WidgetProps) {
  const host = useEditorHost();
  const summary = value === null || value === undefined ? 'None' : Array.isArray(value) ? `${value.length} item${value.length === 1 ? '' : 's'}` : typeof value === 'string' ? value.slice(0, 8) + '…' : 'set';
  return (
    <Button size="s" variant="ghost" onClick={() => owner && host?.open(owner, undefined)} title={`Edit ${p.displayName ?? p.name} in the document`} disabled={!owner || !host}>
      {summary} <Pencil aria-hidden="true" />
    </Button>
  );
}

function TemplateRefSelect({ param: p, value, onCommit, disabled, id, compact }: WidgetProps) {
  const catalog = useContent();
  const options = useMemo(() => catalog.templateList.map((t) => ({ value: t.name, label: t.displayName ?? t.name, hint: t.name })).sort((a, b) => a.label.localeCompare(b.label)), [catalog.templateList]);
  return <Combobox id={id} size={compact ? 's' : 'm'} value={typeof value === 'string' ? value : null} options={options} onChange={(v) => onCommit(v)} emptyLabel="None" disabled={disabled} aria-label={p.displayName ?? p.name} />;
}

/** The dotted paths of the profile tree (section 5.5), typed with completion; free text is allowed. */
function ProfilePathPicker({ param: p, value, onCommit, disabled, id, compact, onCancel }: WidgetProps) {
  const catalog = useContent();
  const paths = useMemo(() => profilePaths(catalog.templateList), [catalog.templateList]);
  const listId = `${id ?? p.name}-paths`;
  return (
    <>
      <CommitTextInput id={id} size={compact ? 's' : 'm'} list={listId} value={typeof value === 'string' ? value : ''} onCommit={(v) => onCommit(v)} disabled={disabled} aria-label={p.displayName ?? p.name} placeholder="GeneralInfo.Level" onKeyDown={(e) => e.key === 'Escape' && onCancel?.()} />
      <datalist id={listId}>
        {paths.map((path) => (
          <option key={path} value={path} />
        ))}
      </datalist>
    </>
  );
}

/** Every `Root.Section.Field` path a profile root reaches (depth-limited). */
export function profilePaths(templates: { name: string; kind: string; effectiveParameters: { name: string; type: string; template?: string | null }[] }[]): string[] {
  const byName = new Map(templates.map((t) => [t.name, t]));
  const out: string[] = [];
  const walk = (t: (typeof templates)[number], prefix: string, depth: number) => {
    for (const p of t.effectiveParameters) {
      const path = prefix ? `${prefix}.${p.name}` : p.name;
      out.push(path);
      if (p.type === 'profile_data' && p.template && depth < 6) {
        const nested = byName.get(p.template);
        if (nested) walk(nested, path, depth + 1);
      }
    }
  };
  for (const root of templates.filter((t) => t.kind === 'profile_root')) walk(root, '', 0);
  return out;
}

/** Untyped JSON: a text area validated when it commits; an invalid text is an inline error and the value stays. */
export function JsonField({ param: p, value, onCommit, disabled, id, compact }: WidgetProps) {
  const [text, setText] = useState(() => (value === undefined ? '' : JSON.stringify(value, null, compact ? 0 : 2)));
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const shown = editing ? text : value === undefined ? '' : JSON.stringify(value, null, compact ? 0 : 2);
  const commit = () => {
    setEditing(false);
    if (shown.trim() === '') {
      setError(null);
      onCommit(null);
      return;
    }
    try {
      const parsed = JSON.parse(shown) as Json;
      setError(null);
      if (JSON.stringify(parsed) !== JSON.stringify(value ?? null)) onCommit(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'invalid JSON');
    }
  };
  return (
    <span className="stack">
      <TextArea id={id} value={shown} rows={compact ? 1 : 4} className={compact ? 'input-inline' : ''} onFocus={() => { setEditing(true); setText(shown); }} onChange={(e) => setText(e.target.value)} onBlur={commit} disabled={disabled} aria-label={p.displayName ?? p.name} aria-invalid={error ? true : undefined} spellCheck={false} />
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
